import axios from "axios";

/**
 * Class for conditional logic in the Livingwishes account form.
 *
 * @property {HTMLElement} wrapperElement All logic is scoped within this container.
 * @property {String} conditionAttribute The attribute for input fields which may cause a change.
 * @property {String} dependentAttribute The attribute for elements that suffer conditions based on the aforementioned change.
 */
class ConditionalLogic {
    constructor(wrapperElement) {
        this.wrapperElement = wrapperElement;
        this.conditionAttribute = 'data-option-id';
        this.dependentAttribute = 'data-depends-on-option';
    }

    /**
     * Load the logic and event handlers.
     */
    initialize() {
        // Check all dependent elements
        this.processDependentElements()

        // Add eventListener to check again when something changes
        this.wrapperElement.querySelectorAll(`[${this.conditionAttribute}]`)
            .forEach((conditionalElement) => {
                // Do not subscribe unnecessary listeners to conditions without a matching value
                const matcherValue = conditionalElement.getAttribute(this.conditionAttribute);
                if (!matcherValue) {
                    return;
                }

                // Options don't have a change event, so attach the event to the parent `select` element.
                if (conditionalElement.tagName === 'OPTION') {
                    conditionalElement = conditionalElement.parentElement;
                }

                // Subscribe listeners to all conditional inputs
                conditionalElement.addEventListener('change', () => {
                    this.processDependentElements()
                });
            });
    }

    /**
     * Check all dependent elements with their conditions, and hide or show them accordingly.
     */
    processDependentElements() {
        this.wrapperElement.querySelectorAll(`[${this.dependentAttribute}]`)
            .forEach((dependentElement) => this.handleDependency(dependentElement));
    }

    /**
     * Check if a dependent element can be shown, based on its conditions.
     * Hides or shows the element accordingly.
     *
     * @param {HTMLElement} dependentElement
     */
    handleDependency(dependentElement) {
        const matcherValue = dependentElement.getAttribute(this.dependentAttribute);
        if (!matcherValue) {
            return;
        }

        // Condition can be 'one-of-many', so iterate over the array
        const valuesToMatch = matcherValue.split(',');
        let conditionIsFulfilled = false;
        valuesToMatch.forEach((valueToMatch) => {
            if (this.checkIfconditionIsFulfilled(valueToMatch)) {
                conditionIsFulfilled = true;
            }
        });

        if (conditionIsFulfilled) {
            this.showElement(dependentElement);
        } else {
            this.hideElement(dependentElement);
        }
    }

    /**
     * Check whether the condition is fulfilled.
     * A condition has been fulfilled when one of the conditional element has an attribute that matches the provided value.
     *
     * @param {int|String} value
     * @return {boolean}
     */
    checkIfconditionIsFulfilled(value) {
        const conditionalElement = this.wrapperElement.querySelector(`[${this.conditionAttribute}="${value}"]`);

        if (!conditionalElement) {
            return false;
        }

        if (conditionalElement.checked) {
            return true;
        }

        if (conditionalElement.selected) {
            return true;
        }

        return false;
    }

    /**
     * Hide an HTMLElement
     *
     * @param {Element} element
     */
    hideElement(element) {
        if (!element || element.classList.contains('d-none')) {
            return;
        }

        element.classList.add('d-none');
    }

    /**
     * Un-hide an HTMLElement
     *
     * @param {Element} element
     */
    showElement(element) {
        if (!element || !element.classList.contains('d-none')) {
            return;
        }

        element.classList.remove('d-none');
    }
}

export default () => {
    const wrapperElement = document.querySelector('#livingwish-account-form');

    if (wrapperElement) {
        // Initialize the handler for conditional logic.
        const conditionalLogic = new ConditionalLogic(wrapperElement);
        conditionalLogic.initialize();

        // Fix range slider that have multiple selected options
        wrapperElement.querySelectorAll('select').forEach((selectElement) => {
            const selectedOptionElements = selectElement.querySelectorAll('option[selected]');

            if (selectedOptionElements.length > 1) {
                if (selectElement.classList.contains('range-option-min')) {
                    selectElement.value = selectedOptionElements[0].value;
                } else if (selectElement.classList.contains('range-option-max')) {
                    selectElement.value = selectedOptionElements[selectedOptionElements.length - 1].value;
                }
            }
        })
    }

    const checkForReachedSelection = () => {
        const modal = document.getElementById('livingwishQuestionPrioritySelectionModal')
        const count = document.getElementById('livingwishQuestionPrioritySelectionModal').querySelectorAll('input[type="checkbox"]:checked').length

        if (!count) {
            modal.querySelector('.btn.btn-primary').classList.add('disabled')
            modal.querySelector('.minimum-message').classList.remove('d-none')
        } else {
            modal.querySelector('.btn.btn-primary').classList.remove('disabled')
            modal.querySelector('.minimum-message').classList.add('d-none')
        }

        // enable all checkboxes when count is less than 4. Otherwise, disable
        document.getElementById('livingwishQuestionPrioritySelectionModal').querySelectorAll('input[type="checkbox"]:not(:checked)').forEach(input => {
            if (count < 4) {
                input.removeAttribute('disabled')
                input.nextElementSibling.classList.remove('disabled')
            } else {
                input.setAttribute('disabled', 'true')
                input.nextElementSibling.classList.add('disabled')
            }
        })
    }

    // do on startup
    checkForReachedSelection()

    const prioritiesModal = document.getElementById('livingwishQuestionPrioritySelectionModal')
    prioritiesModal.querySelectorAll('input[type=checkbox]').forEach(input => {
        input.addEventListener('change', e => {
            checkForReachedSelection()
        })
    })

    // save button
    prioritiesModal.querySelector('.btn.btn-primary').addEventListener('click', async (event) => {
        let questionIds = []
        prioritiesModal.querySelectorAll('input[type="checkbox"]').forEach(input => {
            if (input.checked) {
                questionIds.push(input.value)
            }
        })

        // save priorities and after save the livingwish form
        await axios.post('/account/api/ajax-save-living-wishes-priorities', {questionIds: questionIds})
        document.getElementById('livingwish-account-form').submit()
    })
};